import React, { useState, useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { httpService } from "../../base/services/httpService.service";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { registerDonor } from "./authSlice";
import { useDispatch } from "react-redux";
import TermsConditionsModal from "../../components/TermsConditionsModal";
import { useSelector } from "react-redux";
import Toast from "../../base/components/Toast";
import { Input, Select, Spin } from "antd";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";
import SignupLayout, {
   NO_AUTOFILL,
   ArrowIcon,
   SignupStepper,
   validateSignupStep,
   wizardEnterHandler
} from "./components/SignupLayout";

const initialValues = {
   organizationName: "",
   baseCountryId: null,
   email: "",
   password: "",
   confirmPassword: "",
   invitationCode: "",
   acceptTerms: false
};

// Fields validated before "Continue" moves on from each step.
const STEP_FIELDS = [
   ["email", "password", "confirmPassword", "acceptTerms"],
   ["organizationName", "baseCountryId", "invitationCode"]
];

function RegistrationOfInternationalDonor() {
   const { t } = useTranslation();
   const [step, setStep] = useState(0);
   const [countryList, setCountryList] = useState([]);
   const [modalVisible, setModalVisible] = useState(false);
   const loading = useSelector((state) => state.auth.isLoading);
   const { executeRecaptcha } = useGoogleReCaptcha();

   const openTermsModal = () => {
      setModalVisible(!modalVisible);
   };

   const dispatch = useDispatch();
   const navigate = useNavigate();

   const steps = [t("signup-step-account"), t("profile-organization-label")];

   const validationSchema = Yup.object().shape({
      email: Yup.string()
         .email(t("invalid-email-address-validation"))
         .required(t("email-required-validation")),
      password: Yup.string()
         .min(8, t("password-min-length-validation"))
         .matches(
            /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*\d).*$/,
            t("password-complexity-validation")
         )
         .required(t("password-required-validation")),
      confirmPassword: Yup.string()
         .oneOf([Yup.ref("password"), null], t("passwords-must-match-validation"))
         .required(t("confirm-password-required-validation")),
      organizationName: Yup.string().required(t("donor-organization-name-required-validation")),
      baseCountryId: Yup.string()
         .nullable()
         .required(t("base-country-required-validation")),
      invitationCode: Yup.string()
         .matches(/^[a-zA-Z0-9-]*$/, t("invalid-invitation-code-format-validation"))
         .min(12, t("invitation-code-min-length-validation"))
         .max(12)
         .required(t("invitation-code-required-validation")),
      acceptTerms: Yup.bool().oneOf([true], t("accept-terms-required-validation"))
   });

   useEffect(() => {
      httpService.get(`/countries`, (data) => {
         const countryData = data.data.map((country) => {
            return {
               value: country.id,
               label: country.name
            };
         });
         setCountryList(countryData);
      });
   }, []);

   const onSubmit = async (values) => {
      if (!executeRecaptcha) {
         return;
      }

      const recaptchaToken = await executeRecaptcha("login");
      try {
         const updatedValues = {
            ...values
         };
         dispatch(
            registerDonor(
               updatedValues,
               () => {
                  navigate(`/auth/login`);
               },
               recaptchaToken
            )
         );
      } catch (error) {
         if (error.response || error.response.data || error.response.data.errors) {
            const errorMessage = error.response.data.errors.message;
            Toast.error(errorMessage);
         }
      }
   };

   const goToNextStep = async (formikProps) => {
      if (await validateSignupStep(formikProps, STEP_FIELDS[step])) {
         setStep(step + 1);
         window.scrollTo(0, 0);
      }
   };

   const fieldError = (errors, touched, name) =>
      errors[name] && touched[name] && <div className="error-message">{errors[name]}</div>;

   const isLastStep = step === STEP_FIELDS.length - 1;

   return (
      <SignupLayout
         showcase="donor"
         {...(step === 0 ? { backTo: "/auth/registration" } : { onBack: () => setStep(step - 1) })}
      >
         <p className="kap-org-signup__eyebrow">{t("signup-create-account-as-a")}</p>
         <h1 className="kap-org-signup__title">{t("profile-donor-label")}</h1>

         <SignupStepper steps={steps} current={step} />

         <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
         >
            {(formikProps) => {
               const { values, setFieldValue, errors, touched, handleChange, handleBlur } =
                  formikProps;
               return (
                  <Form
                     className="kap-login__form"
                     style={{ maxWidth: "none" }}
                     onKeyDown={wizardEnterHandler(isLastStep, () => goToNextStep(formikProps))}
                  >
                     {step === 0 && (
                        <>
                           <p className="kap-org-signup__section">{t("login-details-title")}</p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("email")}</label>
                                 <Field
                                    placeholder={t("email-placeholder")}
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "email")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("password-label")}</label>
                                 <Input.Password
                                    placeholder={t("password-label")}
                                    name="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    maxLength={16}
                                 />
                                 {fieldError(errors, touched, "password")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("confirm-password-label")}
                                 </label>
                                 <Input.Password
                                    placeholder={t("password-label")}
                                    name="confirmPassword"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    maxLength={16}
                                 />
                                 {fieldError(errors, touched, "confirmPassword")}
                              </div>
                           </div>
                           <div className="kap-org-signup__terms">
                              <label className="d-flex align-items-start m-0">
                                 <Field type="checkbox" name="acceptTerms" />
                                 <span>
                                    {t("terms-agreement-prefix-text")}{" "}
                                    <Link onClick={openTermsModal} to="#">
                                       {t("terms-of-service-privacy-policy-link")}
                                    </Link>{" "}
                                    {t("and-conjunction-text")}{" "}
                                    <Link onClick={openTermsModal} to="#">
                                       {t("cookie-policy-link")}
                                    </Link>
                                 </span>
                              </label>
                              {fieldError(errors, touched, "acceptTerms")}
                           </div>
                        </>
                     )}

                     {step === 1 && (
                        <>
                           <p className="kap-org-signup__section">
                              {t("organization-details-tab")}
                           </p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("organization-name-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-organization-name-placeholder")}
                                    type="text"
                                    name="organizationName"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "organizationName")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("base-country-label")}
                                 </label>
                                 <Select
                                    showSearch
                                    autoComplete={NO_AUTOFILL}
                                    filterOption={(input, option) =>
                                       option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    placeholder={t("select-base-country-placeholder")}
                                    value={values.baseCountryId ?? undefined}
                                    options={countryList}
                                    onChange={(selectedOption) =>
                                       setFieldValue("baseCountryId", selectedOption)
                                    }
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "baseCountryId")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("invitation-code-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-invitation-code-placeholder")}
                                    type="text"
                                    name="invitationCode"
                                    className="form-control"
                                    autoComplete="off"
                                    maxLength={12}
                                 />
                                 {fieldError(errors, touched, "invitationCode")}
                              </div>
                           </div>
                        </>
                     )}

                     {/* Distinct keys so React swaps the element instead of flipping one
                         button to type="submit" mid-click, which would submit the form. */}
                     {isLastStep ? (
                        <button
                           key="submit"
                           type="submit"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           disabled={loading}
                        >
                           {loading ? <Spin /> : t("sign-up-link")}
                        </button>
                     ) : (
                        <button
                           key="continue"
                           type="button"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           onClick={() => goToNextStep(formikProps)}
                        >
                           {t("continue-button")}
                           <ArrowIcon />
                        </button>
                     )}

                     {isLastStep && (
                        <p className="kap-org-verify__help">
                           {t("no-invitation-code-help-text")}{" "}
                           <a href="mailto:info@kaptechgroup.com">info@kaptechgroup.com</a>
                        </p>
                     )}
                  </Form>
               );
            }}
         </Formik>
         <TermsConditionsModal visible={modalVisible} onCancel={openTermsModal} />
      </SignupLayout>
   );
}
export default RegistrationOfInternationalDonor;
